import { useState } from "react";
import { Alert, Button, Chip, CircularProgress, Collapse, TextField, Typography } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { GROUPS, type GroupKey, type ManualRefundsNotice, type OnboardingAnswers, type OnboardingPreview } from "../../types/onboarding";
import { GROUP_LABELS, partyLabel } from "./labels";

const toManualRefundsText = ({ brandName, platform, autoRefundsIn }: ManualRefundsNotice) => (autoRefundsIn.length
  ? `Agents will refund ${brandName}'s alerts on orders in ${platform} by hand; ${autoRefundsIn.join(" and ")} still auto-refunds the rest.`
  : `Agents will refund ${brandName}'s alerts by hand in ${platform}.`);

export default function ReviewSection({ answers, set, preview, previewError, submitError }: {
  answers: OnboardingAnswers;
  set: (patch: Partial<OnboardingAnswers>) => void;
  preview: OnboardingPreview | null;
  previewError: string | null;
  submitError: string | null;
}) {
  const [open, setOpen] = useState<Set<GroupKey>>(new Set());
  const groups = GROUPS
    .map((key) => ({ key, items: preview?.items.filter((item) => item.groupKey === key) ?? [] }))
    .filter((group) => group.items.length > 0);
  const allOpen = groups.length > 0 && groups.every((g) => open.has(g.key));
  const toggle = (key: GroupKey) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <>
      {submitError && <Alert severity="error">{submitError}</Alert>}
      {previewError && <Alert severity="error">Could not build the checklist preview: {previewError}</Alert>}
      {preview?.notices.paypalOnlyNoAlerts && (
        <Alert severity="warning">PayPal has no alerts product, so this client gets no enrolment steps.</Alert>
      )}
      {!!preview?.notices.manualRefunds.length && (
        <Alert severity="warning">
          Auto-refund only works in Shopify and CheckoutChamp. {preview.notices.manualRefunds.map(toManualRefundsText).join(" ")}
        </Alert>
      )}

      {!preview && !previewError && <CircularProgress size={24} />}
      {preview && (
        <>
          <div className="flex items-baseline justify-between">
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>This creates {preview.items.length} checklist steps</Typography>
            <Button onClick={() => setOpen(allOpen ? new Set() : new Set(groups.map((g) => g.key)))}>
              {allOpen ? "Collapse all" : "Expand all"}
            </Button>
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
            {groups.map((group) => (
              <div key={group.key} className="border-b border-gray-200 last:border-b-0 dark:border-gray-800">
                <button
                  type="button" aria-expanded={open.has(group.key)} onClick={() => toggle(group.key)}
                  className="flex min-h-[52px] w-full cursor-pointer items-center gap-3 px-4 py-3 text-left"
                >
                  <span className="flex-1 text-sm font-medium text-gray-800 dark:text-white/90">{GROUP_LABELS[group.key]}</span>
                  <Chip label={group.items.length} size="small" color="primary" variant="outlined" />
                  <ChevronDown size={16} className={`transition-transform ${open.has(group.key) ? "rotate-180" : ""}`} />
                </button>
                <Collapse in={open.has(group.key)} unmountOnExit>
                  <div className="flex flex-col px-4 pb-3">
                    {group.items.map((item, i) => (
                      <div key={`${item.itemKey}-${i}`} className="flex items-center gap-3 border-t border-gray-100 py-2 text-sm dark:border-gray-800">
                        <span className="flex-1 text-gray-700 dark:text-gray-300">{item.title}</span>
                        <Chip label={partyLabel(item)} size="small" />
                      </div>
                    ))}
                  </div>
                </Collapse>
              </div>
            ))}
          </div>
        </>
      )}

      <TextField
        label="Notes from the sales call" value={answers.salesNotes} multiline minRows={3} fullWidth
        onChange={(e) => set({ salesNotes: e.target.value })}
      />
    </>
  );
}
